/**
 * Desktop-only auto-recovery for the local companion subprocess.
 *
 * Symptoms this fixes (Z-1301): after a long macOS sleep — typically lid
 * closed overnight — the Rust companion engine's subprocess stays
 * alive but its WebSocket task is wedged because the underlying TCP socket
 * was frozen and `time.monotonic()` did not advance during sleep. The
 * supervisor sees `try_wait() == None` and does nothing. Django times out
 * the consumer after ~90s and pushes `companion.status: disconnected` to
 * the owner WebView via [[companion-frame-reducer]], which lands in the
 * store's `setStatus` and (after the grace period in [[companion-store]])
 * surfaces the wrench.
 *
 * This hook watches that committed `disconnected` state and bounces the
 * subprocess via `companion_restart`, including after a launch whose first
 * start never succeeded. The first attempt waits a 30s grace (5s if this
 * session was connected before), then backs off 60s → 120s → 300s. It gives
 * up after four automatic attempts, which also bounds an eviction (4001)
 * ping-pong between installs, and re-arms on window focus, network
 * `online`, or any user reconnect. A tick never overlaps an in-flight
 * restart or a manual reconnect still verifying its handshake; the next one
 * is scheduled only after it settles.
 *
 * Manual reconnects (wrench, toast) bypass the automatic schedule — see
 * [[companion-reconnect-action]].
 */
import { useEffect, useRef, useState } from "react";
import { useCompanionStore } from "@/stores/companion-store";
import { useWindowRole } from "./use-window-role";
import { isTauri } from "@/lib/is-tauri";
import {
  isCompanionRestartInFlight,
  restartCompanion,
} from "./companion-restart";
import { isCompanionReconnectInFlight } from "./companion-reconnect-action";

const FIRST_DIAL_GRACE_MS = 30_000;
const POST_CONNECTION_RESTART_DELAY_MS = 5_000;
const RESTART_BACKOFF_MS: readonly number[] = [60_000, 120_000];
const MAX_RESTART_BACKOFF_MS = 300_000;
const STABLE_CONNECTION_MS = 2 * 60_000;
const MAX_AUTO_RESTART_ATTEMPTS = 4;

const rearmListeners = new Set<() => void>();

/** A user reconnect (or a fresh start failure) restores the full retry budget. */
export function rearmCompanionAutoReconnect(): void {
  for (const listener of rearmListeners) listener();
}

export function nextAutoRestartDelay(
  hasEverConnected: boolean,
  attempts: number,
): number {
  if (attempts === 0) {
    return hasEverConnected
      ? POST_CONNECTION_RESTART_DELAY_MS
      : FIRST_DIAL_GRACE_MS;
  }
  return RESTART_BACKOFF_MS[attempts - 1] ?? MAX_RESTART_BACKOFF_MS;
}

export function useCompanionReconnect({ enabled }: { enabled: boolean }): void {
  const role = useWindowRole();
  const status = useCompanionStore((s) => s.status);
  const hasToken = useCompanionStore((s) => s.hasToken);
  const active = enabled && isTauri() && role === "owner";
  const attempts = useRef(0);
  const hasEverConnected = useRef(false);
  const connectedAt = useRef<number | null>(null);
  const [armedAt, setArmedAt] = useState(0);

  useEffect(() => {
    // Sign-out or an account switch: the next account starts from scratch.
    if (hasToken !== null) return;
    attempts.current = 0;
    hasEverConnected.current = false;
    connectedAt.current = null;
  }, [hasToken]);

  useEffect(() => {
    if (status === "connected") {
      hasEverConnected.current = true;
      connectedAt.current = Date.now();
      return;
    }
    if (
      connectedAt.current !== null &&
      Date.now() - connectedAt.current >= STABLE_CONNECTION_MS
    ) {
      attempts.current = 0;
    }
    connectedAt.current = null;
  }, [status]);

  useEffect(() => {
    if (!active) return;
    const rearm = () => {
      attempts.current = 0;
      setArmedAt(Date.now());
    };
    const rearmIfExhausted = () => {
      if (attempts.current >= MAX_AUTO_RESTART_ATTEMPTS) rearm();
    };
    rearmListeners.add(rearm);
    window.addEventListener("focus", rearmIfExhausted);
    window.addEventListener("online", rearm);
    return () => {
      rearmListeners.delete(rearm);
      window.removeEventListener("focus", rearmIfExhausted);
      window.removeEventListener("online", rearm);
    };
  }, [active]);

  useEffect(() => {
    // `hasToken === null` means the token state is still unknown.
    if (!active || hasToken !== true || status !== "disconnected") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (cancelled || attempts.current >= MAX_AUTO_RESTART_ATTEMPTS) return;
      timer = setTimeout(
        () => {
          if (isCompanionRestartInFlight() || isCompanionReconnectInFlight()) {
            schedule();
            return;
          }
          attempts.current += 1;
          void restartCompanion().finally(schedule);
        },
        nextAutoRestartDelay(hasEverConnected.current, attempts.current),
      );
    };
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, armedAt, hasToken, status]);
}
